// BackgroundChecksStatus - friendly status for the public-records
// systems we automatically check on every person.
//
// Replaces SourceStatusStrip's pill row. The pills were dense and used
// internal jargon (MPA / PGPA / JOCPA / etc.) that confuses the
// non-technical NJRC staff doing intake. This component:
//
//   1. Leads with ONE plain-English status sentence - the only thing
//      a triage user needs at a glance ("Still searching public records,
//      4 of 7 finished" or "All searches complete").
//   2. Hides per-source detail behind a "Show details" expander, so
//      power users (admins, you, Damon when debugging) can drill in.
//   3. Uses friendly source names - "Municipal Court" not "MPA",
//      "NJ Prison" not "DOC", etc.
//   4. Surfaces plain-English error states using the classifier's
//      `explanation` field rather than raw exception messages.

import { useState } from "react";
import type { ExternalLookup, ExternalSource } from "./ExternalSourcesPanel";
import { ALL_SOURCES, type SourceKey } from "../lib/externalSources";

// ─────────────────────────────────────────────────────────────────────────
// Source registry - friendly names and display order live in
// lib/externalSources.ts (shared with PersonPage's pending-banner copy).
// MPA isn't an external_lookup row (it's the main MCCS scrape) - its
// state comes from job-level props, but we render it in the same list
// so all 8 checks are visible together.
// ─────────────────────────────────────────────────────────────────────────

const SOURCES = ALL_SOURCES;

// ─────────────────────────────────────────────────────────────────────────
// Per-row status derivation
// ─────────────────────────────────────────────────────────────────────────

type RowStatus =
  | { kind: "found"; count: number }   // ran + got hits
  | { kind: "clean" }                   // ran + nothing on file
  | { kind: "running" }                 // queued or in-flight
  | { kind: "needs-help"; reason: string }   // failure that won't auto-recover
  | { kind: "retrying"; reason: string }     // failure that auto-retries
  | { kind: "skipped"; reason: string }      // not run because input data missing
  | { kind: "untried" };                 // no row yet

/** Mirror of the server's missingInputsReason in src/server/lib/externalFanout.ts.
 *  Returns a friendly skip reason when this source can't run on the given
 *  person, or null if the source is applicable. Lets the UI render
 *  "skipped - no driver's license on file" instead of leaving the row
 *  in a misleading "untried" state forever. Keep these two in sync. */
function inputsMissingReason(
  source: SourceKey,
  inputs: { licenseNumber: string | null; ssn: string | null; zipCode: string | null },
): string | null {
  if (source === "mvc-surcharge") {
    if (!inputs.licenseNumber?.trim()) return "no driver's license on file";
    return null;
  }
  if (source === "mvc-restoration") {
    if (!inputs.licenseNumber?.trim()) return "no driver's license on file";
    if (!inputs.ssn?.trim()) return "no SSN on file (restoration-fees lookup needs it)";
    if (!inputs.zipCode?.trim()) return "no ZIP on file (restoration-fees lookup needs it)";
    return null;
  }
  return null;
}

function deriveExternalStatus(lookup: ExternalLookup | null | undefined): RowStatus {
  if (!lookup) return { kind: "untried" };
  if (lookup.status === "queued" || lookup.status === "running") return { kind: "running" };
  // Source was paused mid-flight (e.g. JOCPA deactivated server-side).
  // Don't claim "clean" - we never finished. Show as skipped so the
  // operator knows there's no result to act on without rerunning.
  if (lookup.status === "canceled") {
    return { kind: "skipped", reason: "scrape paused - re-run when source is back online" };
  }
  if (lookup.status === "error" || lookup.status === "blocked") {
    // Classifier already produced friendly text - use it. Fall back to
    // a generic message if no classifier output is attached (older rows).
    const friendly = lookup.errorClass?.explanation
      ?? "Couldn't reach this source.";
    const willRetry = lookup.errorClass?.retry.likelihood === "good"
      || lookup.errorClass?.retry.likelihood === "cooldown";
    return willRetry
      ? { kind: "retrying", reason: friendly }
      : { kind: "needs-help", reason: friendly };
  }
  // completed
  if (!lookup.raw_json) return { kind: "clean" };
  try {
    const obj = JSON.parse(lookup.raw_json) as Record<string, unknown>;
    const total =
      (obj.totalRows as number | undefined) ??
      (obj.totalCases as number | undefined) ??
      (obj.totalJudgments as number | undefined) ??
      0;
    if (obj.noResults || obj.noMatch || obj.loginFailed || total === 0) {
      // Specific check: MVC sources have a `totalOwed` / `totalDue` -
      // surface that as "found" with the dollar amount, not "clean".
      const owed =
        (obj.totalOwed as number | undefined) ??
        (obj.totalDue as number | undefined);
      if (typeof owed === "number" && owed > 0) {
        return { kind: "found", count: 1 };
      }
      return { kind: "clean" };
    }
    return { kind: "found", count: total };
  } catch {
    return { kind: "clean" };
  }
}

function deriveMpaStatus(
  running: boolean,
  recordCount: number,
  errored: boolean,
  errorMessage: string | null,
): RowStatus {
  if (running) return { kind: "running" };
  // Errored before completing → don't claim "clean"; that would mislead
  // staff into thinking we LOOKED and found nothing, when actually we
  // never finished. MCCS errors typically auto-retry within the worker
  // (CAPTCHA exhaustion / partial-retry exhaustion are rare); treat as
  // needs-help so staff can re-submit if it stays stuck.
  if (errored) {
    return {
      kind: "needs-help",
      reason: errorMessage ?? "The court-records scrape didn't finish.",
    };
  }
  if (recordCount > 0) return { kind: "found", count: recordCount };
  return { kind: "clean" };
}

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────

interface Props {
  /** Count of MCCS / Municipal records we've already pulled. */
  mpaRecordCount: number;
  /** Whether the main MCCS scrape itself is still in flight. */
  mpaRunning: boolean;
  /** True when the MCCS scrape ended in `error` status. Lets us render
   *  Municipal Court as needs-help instead of the misleading "clean". */
  mpaErrored: boolean;
  /** Optional friendly error message from the MCCS job. Shown in the
   *  expanded detail row when mpaErrored is true. */
  mpaErrorMessage: string | null;
  /** External-lookup map from PersonPage's useExternalLookups. */
  externalLatest: Partial<Record<SourceKey, ExternalLookup | null>>;
  /** True until the parent's first fetch resolves. */
  externalLoading: boolean;
  /** Job-level identifiers - used to detect sources that were skipped
   *  on intake because their required inputs aren't on file (e.g. MVC
   *  scrapers when no DL was provided). Without this, those sources
   *  show as "untried" forever, leaving staff to wonder if the worker
   *  is just slow when actually it's a data-availability issue. */
  personInputs: {
    licenseNumber: string | null;
    ssn: string | null;
    zipCode: string | null;
  };
  /** Manual-retry handler for sources that won't auto-recover. Wired
   *  to PersonPage's externalEnqueue so the user can re-run a failed
   *  source without leaving the page (or hunting for the matching
   *  source card in ExternalSourcesPanel). */
  onRetry: (source: ExternalSource) => Promise<void>;
  /** "Add the missing identifier" handler. Fired when the user clicks
   *  the "Add now" button on a row that's skipped because of missing
   *  intake info. PersonPage opens an IdentifiersModal pre-focused on
   *  the right field. */
  onAddIdentifier: (field: "dl" | "ssn" | "zip") => void;
  /** When true, the per-source detail list is open by default. */
  defaultExpanded?: boolean;
}

/** Map a skipped source to the FIRST missing identifier field. The
 *  IdentifiersModal opens with that one focused. mvc-surcharge only
 *  needs DL; mvc-restoration needs DL + SSN + ZIP. */
function firstMissingField(
  source: SourceKey,
  inputs: { licenseNumber: string | null; ssn: string | null; zipCode: string | null },
): "dl" | "ssn" | "zip" | null {
  if (source === "mvc-surcharge" || source === "mvc-restoration") {
    if (!inputs.licenseNumber?.trim()) return "dl";
    if (source === "mvc-restoration") {
      if (!inputs.ssn?.trim()) return "ssn";
      if (!inputs.zipCode?.trim()) return "zip";
    }
  }
  return null;
}

export function BackgroundChecksStatus({
  mpaRecordCount,
  mpaRunning,
  mpaErrored,
  mpaErrorMessage,
  externalLatest,
  externalLoading,
  personInputs,
  onRetry,
  onAddIdentifier,
  defaultExpanded = false,
}: Props): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Per-source retry-busy flag so the inline button can show a "…" state
  // while the request is in flight. Keyed by source - only one button at
  // a time will be busy in practice (user clicks one, waits, clicks next).
  const [retrying, setRetrying] = useState<SourceKey | null>(null);

  const handleRetry = async (key: SourceKey): Promise<void> => {
    if (key === "mpa") return; // not an external_lookups source
    setRetrying(key);
    try {
      await onRetry(key as ExternalSource);
    } catch {
      // Errors surface via the source's row state on the next refresh -
      // no toast needed.
    } finally {
      setRetrying(null);
    }
  };

  // Build the per-source status list once. MPA pulls from job-level state;
  // everything else from external_latest.
  const rows = SOURCES.map((src) => {
    let status: RowStatus;
    if (src.key === "mpa") {
      status = deriveMpaStatus(mpaRunning, mpaRecordCount, mpaErrored, mpaErrorMessage);
    } else if (externalLoading) {
      status = { kind: "untried" };
    } else {
      // Check for missing inputs FIRST: a row that was skipped on
      // Check missing-inputs FIRST - even if the worker has an error
      // row on file. If the scrape requires data we don't have on
      // file (e.g. mvc-surcharge needs a DL number), the root cause
      // is "we're missing info from the LSC", not a true scrape
      // error. Surface it as "needs info" with an actionable button
      // rather than a red error flag the LSC has to interpret.
      const existing = externalLatest[src.key];
      const missingReason = inputsMissingReason(src.key, personInputs);
      if (missingReason) {
        status = { kind: "skipped", reason: missingReason };
      } else if (!existing) {
        status = { kind: "untried" };
      } else {
        status = deriveExternalStatus(existing);
      }
    }
    return { ...src, status };
  });

  // Aggregate counts for the headline sentence. "Done" includes
  // found-with-hits, clean (we ran them), AND skipped (we explicitly
  // chose not to run; that's a final state). Running + retrying both
  // count as in-flight from the user's perspective.
  const totalSources = rows.length;
  const doneCount = rows.filter(
    (r) =>
      r.status.kind === "found" ||
      r.status.kind === "clean" ||
      r.status.kind === "skipped",
  ).length;
  const runningCount = rows.filter(
    (r) => r.status.kind === "running" || r.status.kind === "retrying",
  ).length;
  const needsHelpCount = rows.filter((r) => r.status.kind === "needs-help").length;
  const untriedCount = rows.filter((r) => r.status.kind === "untried").length;
  const skippedCount = rows.filter((r) => r.status.kind === "skipped").length;

  const allDone = doneCount === totalSources;
  const foundCount = rows.filter((r) => r.status.kind === "found").length;

  // Headline sentence - the only thing a triage user needs at a glance.
  let headline: string;
  let accent: "ok" | "running" | "warn";
  if (allDone) {
    const skippedSuffix =
      skippedCount > 0
        ? ` (${skippedCount} skipped: see details for why)`
        : "";
    headline =
      foundCount === 0
        ? `All searches complete: no external records found${skippedSuffix}.`
        : `All searches complete: found records in ${foundCount} ${foundCount === 1 ? "system" : "systems"}${skippedSuffix}.`;
    accent = "ok";
  } else if (needsHelpCount > 0 && runningCount === 0 && untriedCount === 0) {
    // Everything that can finish has finished; remaining sources need
    // operator attention.
    headline = `${doneCount} of ${totalSources} searches done. ${needsHelpCount} need a closer look.`;
    accent = "warn";
  } else if (runningCount === 0 && untriedCount > 0) {
    // Idle but not actually started - usually transient (worker just
    // hasn't picked them up yet).
    headline = `Starting public-records search… ${doneCount} of ${totalSources} done.`;
    accent = "running";
  } else {
    headline = `Searching public records… ${doneCount} of ${totalSources} done.`;
    accent = "running";
  }

  // Accent colors aligned with the bordeaux+gold palette: running uses
  // the cream-gold accent (matches CTAs across the app), ok stays sage,
  // warn keeps amber. No more "system color" feel.
  const accentColor =
    accent === "ok"
      ? "var(--color-sage-hi, #8ed1a8)"
      : accent === "warn"
        ? "var(--color-amber-hi, #ffd166)"
        : "var(--color-accent, #D6BE7A)";

  // Progress bar percentage. Running rows count as "in progress" - show
  // them as a striped/animated portion. We just give a static fill ratio
  // for the done count; running gets visual weight via the accent color.
  const pct = Math.round((doneCount / totalSources) * 100);

  return (
    <section
      className="container station"
      style={{ paddingTop: 0, paddingBottom: 0 }}
      aria-label="Background-records check status"
    >
      {/* Inner card-with-border removed - when this component lives inside
          a page-card, that nesting reads as 3D card-within-card. Flat
          content now; padding/border come from the parent card. */}
      <div>
        {/* Headline line + status dot */}
        <div className="flex items-center gap-3 flex-wrap">
          <span
            aria-hidden="true"
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: accentColor,
              flexShrink: 0,
              // Pulse only while running so the dot doesn't draw attention
              // to a stable end-state.
              // Reuses the existing `pulse` keyframe defined in index.css
              // (the same animation .dot-pulse uses).
              animation: accent === "running" ? "pulse 1.6s ease-in-out infinite" : "none",
            }}
          />
          <span
            className="flex-1 min-w-0"
            style={{
              color: "var(--color-text)",
              fontSize: 14,
              lineHeight: 1.4,
              fontFamily: "var(--font-body)",
              fontWeight: 500,
            }}
          >
            {headline}
          </span>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] uppercase transition-colors hover:text-[color:var(--color-text)]"
            style={{
              color: "var(--color-text-dim)",
              letterSpacing: "0.16em",
              fontFamily: "var(--font-mono)",
              padding: "4px 8px",
              border: "1px solid var(--color-line)",
              borderRadius: 6,
              background: "transparent",
              cursor: "pointer",
            }}
            aria-expanded={expanded}
          >
            {expanded ? "Hide details ▴" : "Show details ▾"}
          </button>
        </div>

        {/* Progress bar */}
        <div
          style={{
            marginTop: 10,
            height: 4,
            borderRadius: 2,
            background: "var(--color-bg-card-hi, rgba(255,255,255,0.06))",
            overflow: "hidden",
          }}
          aria-hidden="true"
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: accentColor,
              transition: "width 0.3s ease",
            }}
          />
        </div>

        {/* Detail list - only renders when expanded. Friendly names + a
            single short status sentence per row. */}
        {expanded && (
          <ul
            className="mt-4 grid gap-2"
            style={{ gridTemplateColumns: "1fr", listStyle: "none", padding: 0 }}
          >
            {rows.map((row) => (
              <li
                key={row.key}
                title={row.tooltip}
                className="flex items-baseline gap-3 py-1"
                style={{
                  fontSize: 13,
                  lineHeight: 1.4,
                  fontFamily: "var(--font-body)",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    width: 16,
                    textAlign: "center",
                    color: rowIconColor(row.status.kind),
                    flexShrink: 0,
                  }}
                >
                  {rowIcon(row.status.kind)}
                </span>
                <span
                  style={{
                    minWidth: 130,
                    color: "var(--color-text)",
                    fontWeight: 500,
                  }}
                >
                  {row.label}
                </span>
                <span
                  className="flex-1 min-w-0 truncate"
                  style={{ color: "var(--color-text-muted)" }}
                >
                  {rowDescription(row.status)}
                </span>
                {/* Inline "Retry" button - only on rows that won't
                    recover on their own AND that are external_lookups
                    sources (MPA isn't manually re-runnable from here).
                    Saves the user from hunting for the matching source
                    card down the page. */}
                {row.status.kind === "needs-help" && row.key !== "mpa" && (
                  <button
                    type="button"
                    onClick={() => void handleRetry(row.key)}
                    disabled={retrying === row.key}
                    className="text-[11px] uppercase rounded px-2 py-0.5 transition-colors hover:bg-[color:var(--color-bg-card-hi)]"
                    style={{
                      color: "var(--color-rose-hi, #f59e9e)",
                      border: "1px solid color-mix(in oklab, var(--color-rose-hi, #f59e9e) 50%, transparent)",
                      letterSpacing: "0.12em",
                      fontFamily: "var(--font-mono)",
                      cursor: retrying === row.key ? "wait" : "pointer",
                      flexShrink: 0,
                    }}
                  >
                    {retrying === row.key ? "Retrying…" : "Retry"}
                  </button>
                )}
                {/* "Add [field]" button on skipped rows - opens the
                    IdentifiersModal pre-focused on whichever field is
                    missing. Closes the gap from "I see what's missing"
                    to "I can fix it" - used to require finding the
                    Personal Details modal somewhere else on the page. */}
                {row.status.kind === "skipped" && (() => {
                  const field = firstMissingField(row.key, personInputs);
                  if (!field) return null;
                  const labels: Record<typeof field, string> = {
                    dl: "Add DL #",
                    ssn: "Add SSN",
                    zip: "Add ZIP",
                  };
                  return (
                    <button
                      type="button"
                      onClick={() => onAddIdentifier(field)}
                      className="text-[11px] uppercase rounded px-2 py-0.5 transition-colors hover:bg-[color:var(--color-bg-card-hi)]"
                      style={{
                        color: "var(--color-amber-hi, #ffd166)",
                        border: "1px solid color-mix(in oklab, var(--color-amber-hi, #ffd166) 50%, transparent)",
                        letterSpacing: "0.12em",
                        fontFamily: "var(--font-mono)",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {labels[field]}
                    </button>
                  );
                })()}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function rowIcon(kind: RowStatus["kind"]): string {
  switch (kind) {
    case "found":      return "✓";
    case "clean":      return "✓";
    case "running":    return "⟳";
    case "retrying":   return "↻";
    case "needs-help": return "!";
    // Skipped = "we couldn't run this because we're missing info" -
    // render as a question mark in amber so the LSC reads it as
    // actionable ("supply info"), not as a passive ⊘ "we gave up."
    case "skipped":    return "?";
    case "untried":    return "·";
  }
}

function rowIconColor(kind: RowStatus["kind"]): string {
  switch (kind) {
    case "found":      return "var(--color-sage-hi, #8ed1a8)";
    case "clean":      return "var(--color-text-dim)";
    case "running":    return "var(--color-amber-hi, #ffd166)";
    case "retrying":   return "var(--color-amber-hi, #ffd166)";
    case "needs-help": return "var(--color-rose-hi, #f59e9e)";
    // Amber (same family as the "Add DL #" button next to it) so the
    // row reads as "your turn, give us info" - distinct from the
    // rose-red needs-help error state.
    case "skipped":    return "var(--color-amber-hi, #ffd166)";
    case "untried":    return "var(--color-text-dim)";
  }
}

function rowDescription(status: RowStatus): string {
  switch (status.kind) {
    case "found":
      return status.count === 1 ? "1 record on file" : `${status.count} records on file`;
    case "clean":
      return "Nothing on file.";
    case "running":
      return "Searching now…";
    case "retrying":
      return `${status.reason} Will try again automatically.`;
    case "needs-help":
      return `${status.reason} Won't retry automatically: click Retry, or escalate.`;
    case "skipped":
      return `Need info - ${status.reason}.`;
    case "untried":
      return "Hasn't started yet.";
  }
}
